/**
 * Base API configuration constants.
 */
import { env } from './env';

export const API_BASE_URL = env.API_ENDPOINT;

export const API_KEY = env.API_KEY;

/**
 * Standard error shape returned by the Lumina API.
 */
export interface ApiError {
  status: number;
  message: string;
  detail?: string;
}

/**
 * Type-guard to check if an error is an ApiError.
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'message' in error
  );
}

/**
 * Extract a user-friendly message from an RTK Query error.
 *
 * API Gateway wraps Lambda errors as:
 *   { "error-type": "ServerError", "message": "<json-string>" }
 * where the inner JSON contains the actual ``message`` field.
 */
export function extractErrorMessage(error: unknown): string {
  const fallback = 'Something went wrong. Please try again.';
  try {
    const data = (error as Record<string, unknown>)?.data;
    if (!data || typeof data !== 'object') return fallback;

    const raw = (data as Record<string, unknown>).message;
    if (typeof raw !== 'string') return fallback;

    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed.message) {
        return parsed.message;
      }
    } catch {
      // raw is not JSON -- use it directly
    }

    return raw || fallback;
  } catch {
    return fallback;
  }
}
